import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { TrendingUp, TrendingDown, DollarSign, BarChart2, ArrowRight, RefreshCw } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import PortfolioChart from '@/components/charts/PortfolioChart'
import { formatCurrency, formatPercent, cn } from '@/lib/utils'
import type { Holding, PortfolioSummary, PortfolioHistoryPoint } from '@/types'

const CHART_RANGES = [
  { label: '7D', days: 7 },
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
]

export default function Dashboard() {
  const { user } = useAuth()
  const [summary, setSummary] = useState<PortfolioSummary | null>(null)
  const [holdings, setHoldings] = useState<Holding[]>([])
  const [history, setHistory] = useState<PortfolioHistoryPoint[]>([])
  const [chartRange, setChartRange] = useState(30)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const load = async (refreshing = false) => {
    if (refreshing) setIsRefreshing(true)
    else setIsLoading(true)

    const [portfolioRes, historyRes] = await Promise.all([
      api.getPortfolio(),
      api.getPortfolioHistory(chartRange),
    ])

    if (portfolioRes.success && portfolioRes.data) {
      setSummary(portfolioRes.data.summary)
      setHoldings(portfolioRes.data.holdings)
    }

    if (historyRes.success && historyRes.data) {
      setHistory(historyRes.data)
    }

    setIsLoading(false)
    setIsRefreshing(false)
  }

  useEffect(() => { load() }, [chartRange])

  const topHoldings = holdings
    .sort((a, b) => (b.total_value ?? 0) - (a.total_value ?? 0))
    .slice(0, 5)

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-white/[0.06] rounded-xl w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-28 bg-white/[0.04] rounded-xl" />
          ))}
        </div>
        <div className="h-72 bg-white/[0.04] rounded-xl" />
      </div>
    )
  }

  const isPositive = (summary?.total_return ?? 0) >= 0

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-white/40 text-sm mt-0.5">
            Welcome back, {user?.username}
          </p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={isRefreshing}
          className="flex items-center gap-2 btn-secondary text-sm"
        >
          <RefreshCw className={cn('w-4 h-4', isRefreshing && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Value"
          value={formatCurrency(summary?.total_value ?? 0)}
          icon={DollarSign}
          accent
        />
        <StatCard
          label="Cash Balance"
          value={formatCurrency(summary?.cash_balance ?? 0)}
          sub={`${(((summary?.cash_balance ?? 0) / (summary?.total_value ?? 1)) * 100).toFixed(0)}% of portfolio`}
          icon={DollarSign}
        />
        <StatCard
          label="Total Return"
          value={formatCurrency(summary?.total_return ?? 0)}
          sub={formatPercent(summary?.total_return_pct ?? 0)}
          icon={isPositive ? TrendingUp : TrendingDown}
          positive={isPositive}
          negative={!isPositive}
        />
        <StatCard
          label="Holdings"
          value={String(summary?.holdings_count ?? 0)}
          sub={`${formatCurrency(summary?.holdings_value ?? 0)} invested`}
          icon={BarChart2}
        />
      </div>

      {/* Chart */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-semibold">Portfolio Performance</h2>
            <p className="text-white/40 text-xs mt-0.5">Total value over time</p>
          </div>
          <div className="flex gap-1">
            {CHART_RANGES.map(({ label, days }) => (
              <button
                key={days}
                onClick={() => setChartRange(days)}
                className={cn(
                  'px-3 py-1 rounded-lg text-xs font-medium transition-all',
                  chartRange === days
                    ? 'bg-accent/15 text-accent'
                    : 'text-white/30 hover:text-white hover:bg-white/[0.05]'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <PortfolioChart data={history} height={220} />
      </div>

      {/* Top holdings */}
      <div className="card">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
          <h2 className="font-semibold">Top Holdings</h2>
          <Link
            to="/portfolio"
            className="text-xs text-accent hover:underline flex items-center gap-1"
          >
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {topHoldings.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <div className="text-white/30 text-sm mb-3">No positions yet</div>
            <Link to="/trade" className="btn-primary text-sm py-2">
              Start Trading
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {topHoldings.map((h) => {
              const pnlPct = h.avg_cost_basis > 0
                ? ((( h.current_price ?? h.avg_cost_basis) - h.avg_cost_basis) / h.avg_cost_basis) * 100
                : 0
              const isUp = (h.unrealized_pnl ?? 0) >= 0

              return (
                <Link
                  key={h.symbol}
                  to={`/trade/${h.symbol}`}
                  className="flex items-center gap-4 px-6 py-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="w-9 h-9 bg-white/[0.06] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-white/70">{h.symbol.slice(0, 2)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{h.symbol}</div>
                    <div className="text-xs text-white/40">{h.shares} shares</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm font-medium">
                      {formatCurrency(h.total_value ?? 0)}
                    </div>
                    <div className={cn('text-xs font-mono', isUp ? 'text-gain' : 'text-loss')}>
                      {isUp ? '+' : ''}{formatCurrency(h.unrealized_pnl ?? 0)} ({formatPercent(pnlPct)})
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  accent,
  positive,
  negative,
}: {
  label: string
  value: string
  sub?: string
  icon: React.ElementType
  accent?: boolean
  positive?: boolean
  negative?: boolean
}) {
  return (
    <div className="card p-5 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-white/40 font-medium uppercase tracking-wider">{label}</span>
        <div
          className={cn(
            'w-7 h-7 rounded-lg flex items-center justify-center',
            accent ? 'bg-accent/10' : positive ? 'bg-gain-muted' : negative ? 'bg-loss-muted' : 'bg-white/[0.06]'
          )}
        >
          <Icon
            className={cn(
              'w-3.5 h-3.5',
              accent ? 'text-accent' : positive ? 'text-gain' : negative ? 'text-loss' : 'text-white/40'
            )}
          />
        </div>
      </div>
      <div>
        <div
          className={cn(
            'text-xl font-semibold font-mono',
            positive ? 'text-gain' : negative ? 'text-loss' : ''
          )}
        >
          {value}
        </div>
        {sub && <div className="text-xs text-white/30 mt-0.5">{sub}</div>}
      </div>
    </div>
  )
}
